/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { useDispatch, useSelector } from 'react-redux';

import { setRoomError } from '../../../actions/roomActions';
import { State } from '../../../redux';
import { Tabs } from '../../../components/common/enterprise/Tabs/Tabs';
import { useRoom } from '../../../hooks/useRoom';
import { useNotes } from '../../../hooks/useNotes';
import { useRoomMetadata } from '../../../hooks/useRoomMetadata';
import { useRoomDetail } from '../../../hooks/useRoomDetail';
import { enterpriseColors } from '../../../style/colors';
import { RescheduleModal } from '../RescheduleModal/RescheduleModal';
import { usePermissions } from '../../../hooks/usePermissions';
import { initializeWebsocketEventSubscriber } from '../../../api/websocket/eventSubscriber';
import { WebsocketProviders } from '../../../api/websocket/types';
import { useRootSelector } from '../../../hooks/useRootSelector';
import { appcuesEvents } from '../../../api/appcues/events';
import { ExitSessionConfirmationModal } from '../../../components/NotaryRoom/ExitSessionConfirmationModal/ExitSessionConfirmationModal';
import { notaryAPI } from '../../../api/notary';
import { Participants } from './Participants/Participants';
import { RoomInfoSummary } from './RoomHeader/RoomInfoSummary/RoomInfoSummary';
import { RoomHeaderWrapper } from './RoomHeader/RoomHeader';
import { Sidebar } from './Sidebar';
import { RoomError } from './RoomError';
import { ActivityTab } from './ActivityTab/ActivityTab';
import { OverviewTab } from './OverviewTab/OverviewTab';
import { NotesTab } from './NotesTab/NotesTab';
import { isGroupAdmin } from '@utils/permissions';

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: '100%',
  },
  contentWrapper: {
    width: '100%',
    height: '100%',
    overflow: 'scroll',
    backgroundColor: enterpriseColors.BACKGROUND_GREY,
  },
});

export const Room: React.FC = () => {
  const dispatch = useDispatch();
  const { user: notaryUser } = useRootSelector(state => ({
    user: state.user,
  }));
  const { roomId } = useRoom();
  const { isLoading: notesLoading, dispatchNotes } = useNotes(roomId);
  const { isLoading: roomMetadataLoading } = useRoomMetadata(roomId);
  const { isLoading, loadRoomDetail } = useRoomDetail(roomId);
  const [activeTab, setActiveTab] = useState('overview');
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [callId, setCallId] = useState<string | undefined>(undefined);
  const [callStatus, setCallStatus] = useState<string | undefined>(undefined);
  const { roomDetail } = useSelector((reduxState: State) => reduxState);
  const permissions = usePermissions();
  const isAdmin = isGroupAdmin(permissions);
  const roomNotes = roomDetail.notes?.length ?? 0;
  const lenderInstructions = roomDetail.metadata?.specialInstructions ? 1 : 0;
  const totalNotes = roomNotes + lenderInstructions;
  const showNotes = !notesLoading && !roomMetadataLoading;

  const tabsConfig = [
    {
      id: 'overview',
      label: 'Overview',
      onClick: () => setActiveTab('overview'),
    },
    {
      id: 'participants',
      label: 'Participants',
      onClick: () => setActiveTab('participants'),
    },
    {
      id: 'activity',
      label: 'Activity',
      onClick: () => setActiveTab('activity'),
    },
    {
      id: 'notes',
      label: 'Notes',
      count: showNotes ? totalNotes : undefined,
      onClick: () => setActiveTab('notes'),
    },
  ];

  const handleCloseRescheduleModal = () => {
    setIsRescheduleModalOpen(false);
    loadRoomDetail();
  };

  useEffect(() => {
    if (activeTab === 'overview' && roomNotes > 0) {
      appcuesEvents.highlightNotesTab({ roomId, notaryId: notaryUser.id });
    }
  }, [activeTab, roomNotes]);

  useEffect(() => {
    (async () => await dispatchNotes())();
    initializeWebsocketEventSubscriber(WebsocketProviders.FAYE, roomId);
  }, [roomId]);

  // Fetch current call ID and status for the room
  useEffect(() => {
    const fetchCallDetails = async () => {
      try {
        const callDetails = await notaryAPI.calls.fetchCallDetailsForRoom(roomId);
        setCallId(callDetails.id);
        setCallStatus(callDetails.status);
      } catch (err) {
        setCallId(undefined);
        setCallStatus(undefined);
      }
    };
    fetchCallDetails();
  }, [roomId, roomDetail.room?.isCallInProgress]);

  useEffect(() => {
    if (roomDetail.room && roomDetail.room.isCallInProgress) {
      setRoomError({
        hasError: true,
        message: 'This notarization is in progress: Some actions might not be available at this time.',
        theme: 'warn',
        icon: 'warning',
        canCancel: false,
      })(dispatch);
    }
    return () => {
      setRoomError({ hasError: false, message: undefined })(dispatch);
    };
  }, [roomDetail.room]);

  const getActiveTab = () => {
    if (activeTab === 'overview') {
      return (
        <OverviewTab
          roomId={roomId}
          scheduleTime={roomDetail?.room?.scheduleTime}
          notaryId={roomDetail?.room?.notaryId}
          onReschedule={() => setIsRescheduleModalOpen(true)}
          onCancel={() => loadRoomDetail()}
        />
      );
    }
    if (activeTab === 'participants') {
      return <Participants roomId={roomId} />;
    }
    if (activeTab === 'activity') {
      return <ActivityTab roomId={roomId} />;
    }
    if (activeTab === 'notes') {
      return <NotesTab roomId={roomId} notesLoading={notesLoading} />;
    }
    return;
  };
  return (
    <div className={css(styles.wrapper)}>
      <Sidebar />
      {!isLoading && (
        <div className={css(styles.contentWrapper)}>
          <RoomHeaderWrapper>
            <RoomInfoSummary
              onReschedule={() => setIsRescheduleModalOpen(true)}
              onGroupOverwrite={() => loadRoomDetail()}
              onVideoOverride={() => loadRoomDetail()}
              callStatus={callStatus}
            />
            <Tabs activeTab={activeTab} tabsConfig={tabsConfig} />
          </RoomHeaderWrapper>
          {getActiveTab()}
        </div>
      )}
      <RoomError />
      {roomDetail.room && isRescheduleModalOpen && (
        <RescheduleModal
          isOpen={isRescheduleModalOpen}
          roomId={roomDetail.room.id}
          signers={roomDetail.room.signers}
          scheduleDate={roomDetail.room.scheduleDate}
          scheduleTime={roomDetail.room.scheduleTime}
          preferredTimeSlots={roomDetail.room.preferredTimeSlots}
          preferredTimeZone={roomDetail.room.preferredTimeZone}
          timeZone={roomDetail.room.timeZone}
          handleClose={handleCloseRescheduleModal}
          notaryId={roomDetail.room.notaryId}
          isAdmin={isAdmin}
        />
      )}
      {roomDetail.room && <ExitSessionConfirmationModal roomId={roomDetail.room.id} callId={callId} />}
    </div>
  );
};
